import React, { useState } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
 Stack,
 Button,
 Flex,
 Center,
 Container,
 AccordionPanel,
 AccordionItem,
 AccordionIcon,
 AccordionButton,
 useToast,
 Accordion,
} from '@chakra-ui/react';
import baseService from '../../services/base';
import { companieGroupSchema } from '../../core/validations/companie';
import { useCNPJSearch } from '../../hooks/useCNPJSearch';
import CompanyFields from './CompanyFields';
import AdminFields from './AdminFields';
import CNPJField from './CNPJField';
import EsgStarterCard from './EsgStarterCard';
import FreemiumCard from './FreemiumCard';

export default function CompanyRegisterForm({
 initialValues,
 formsList,
 idCompanyGroup,
 isFreemium,
 handleSuccess,
 companyRegister,
}) {
 const toast = useToast();
 const [cnpjControl, setCnpjControl] = useState([]);
 const [expandedIndexes, setExpandedIndexes] = useState(() => {
  if (!companyRegister && initialValues?.length === 1) {
   return [0];
  }
  return [];
 });

 const formMethods = useForm({
  // EG-70: sem edição, `isFreemium` nunca chega (companyRegister vazio, a
  // tela nem busca /company/:id) — cai no `?? true`, mesmo default da coluna.
  // Companie.jsx só renderiza este form depois que o SWR de /company/:id já
  // respondeu, então o valor carregado já está disponível no mount.
  defaultValues: {
   form: initialValues,
   generateEsgStarter: false,
   isFreemium: isFreemium ?? true,
  },
  resolver: yupResolver(companieGroupSchema),
  mode: 'onChange',
 });

 const {
  handleSubmit,
  control,
  formState: { isDirty, isValid, isSubmitting, dirtyFields },
 } = formMethods;

 const { fields, append, remove } = useFieldArray({
  control: formMethods.control,
  name: 'form',
 });

 const {
  searchCNPJ,
  clearCNPJ,
  isLoading: cnpjSearchIsLoading,
 } = useCNPJSearch({
  fields,
  formMethods,
  cnpjControl,
  setCnpjControl,
 });

 const addNewCompany = () => {
  const newCompanyTemplate = {
   address: '',
   admin: { username: '', email: '' },
   cnae_list: '',
   cnpj: '',
   fantasyname: '',
   name: '',
   phone: '',
   language: 'pt-BR',
   newField: true,
   parent_company: +companyRegister,
  };

  append(newCompanyTemplate);
  setExpandedIndexes((prev) => [...prev, fields.length]);
 };

 const onSubmit = async (data) => {
  const validResponses = data?.form?.map((response, index) => {
    const payload = {
      // Se for uma nova empresa (newField é true), enviar CNPJ com os caracteres
      cnpj: response?.newField ? response?.cnpj : response.cnpj.replace(/[^0-9]/g, ''),
      cnaeList: response?.cnae_list,
      name: response?.name,
      fantasyName: response?.fantasyname,
      phone: response?.phone,
      address: response?.address,
      language: response?.language || 'pt-BR',
    };

    // Include responsible data if it exists
    if (response?.admin?.username && response?.admin?.email) {
      payload.responsible = response.admin.username;
      payload.responsibleEmail = response.admin.email;
    }

    if (companyRegister) {
      const oldAdminValues = initialValues?.[index]?.admin;
      const adminHasUpdate = 
        response?.admin?.email !== oldAdminValues?.email ||
        response?.admin?.username !== oldAdminValues?.username;

      if (adminHasUpdate) {
        payload.updated = true;
      }
    }

    if (response?.id_company) {
      payload.id_company = response?.id_company;
    }

    if (response?.parent_company) {
      payload.parent_company = response?.parent_company;
    }

    return payload;
  });

  try {
    let surveysBody = [];
    if (formsList?.length > 0) {
      surveysBody = formsList.map((survey) => ({
        id_survey: survey.s_id_survey,
        reference_month_year: survey.link_survey_company_reference_month_year,
        end_application_date: survey.link_survey_company_end_application_date,
      }));
    }

    const initialPayload = {
      companies: validResponses,
      surveys: surveysBody,
    };

    if (idCompanyGroup) {
      initialPayload.groups = idCompanyGroup;
    }

    // Só tem efeito para as empresas realmente novas desta submissão — o backend
    // ignora empresas já existentes (edição), então marcar aqui numa edição pura
    // não gera nada.
    initialPayload.generateEsgStarter = !!data?.generateEsgStarter;
    // EG-70: sempre manda o valor atual do toggle — é o único caminho de escrita
    // deste campo, então não há motivo pra omitir (o backend trata ausente como
    // "não mexe", pensado pra outros chamadores do mesmo endpoint).
    initialPayload.isFreemium = !!data?.isFreemium;

    const response = await baseService.post(`/company`, initialPayload);
    
    if (response?.data?.response?.statusCode === 409) {
      throw new Error('409');
    }
    
    handleSuccess();
  } catch (error) {
    if (error?.message === '409') {
      toast({
        title: 'Erro ao atualizar os dados',
        description: 'E-mail do avaliador já existe no sistema',
        status: 'error',
      });
    } else {
      toast({
        title: 'Erro ao atualizar os dados',
        status: 'error',
      });
    }
  }
};

 return (
  <form onSubmit={handleSubmit(onSubmit)}>
   <Accordion allowMultiple index={expandedIndexes} onChange={setExpandedIndexes}>
    {fields?.map((field, index) => (
     <AccordionItem
      key={field?.id}
      index={index}
      px={0}
      mb='22px'
      border='1px solid #ADAFB3'
      borderRadius='8px'
      overflow='hidden'
     >
      <AccordionButton
       px='24px'
       color='#0062FF'
       height='56px'
       fontSize='20px'
       fontWeight={700}
       border='none'
       borderRadius='8px'
       _hover={{ bg: 'none' }}
       display={field?.newField ? 'none' : 'flex'}
      >
       {field?.name}
       <AccordionIcon ml='auto' />
      </AccordionButton>

      <AccordionPanel pb={4} px='6' bg='white'>
       <Stack spacing='24px' pt='20px'>
        <CNPJField
         index={index}
         control={control}
         field={field}
         cnpjControl={cnpjControl}
         isLoading={cnpjSearchIsLoading}
         onSearch={() => searchCNPJ(index)}
         onClear={() => clearCNPJ(index)}
        />

        <CompanyFields index={index} control={control} disabled={cnpjSearchIsLoading} />

        {/* 
        Remove cadastro de responsável junto ao cadastro de empresa
        <AdminFields index={index} control={control} disabled={cnpjSearchIsLoading} /> 
        */}

        {companyRegister && field?.newField && fields?.length > 1 && (
         <Button w='120px' variant='cancel' ml='auto' onClick={() => remove(index)}>
          Remover
         </Button>
        )}
       </Stack>
      </AccordionPanel>
     </AccordionItem>
    ))}
   </Accordion>

   {companyRegister && (
    <Button
     onClick={addNewCompany}
     variant='primary'
     w='208px'
     height='49px'
     isDisabled={!isValid}
    >
     Adicionar empresa
    </Button>
   )}

   <Controller
    name='generateEsgStarter'
    control={control}
    render={({ field }) => (
     <EsgStarterCard
      companyId={companyRegister}
      value={field.value}
      onChange={field.onChange}
     />
    )}
   />

   <Controller
    name='isFreemium'
    control={control}
    render={({ field }) => (
     <FreemiumCard value={field.value} onChange={field.onChange} />
    )}
   />

   <Center pos='absolute' top='0' left='0' right={0} px={{ xl: '86px', '2xl': 'unset' }}>
    <Container maxW='1280px' p={0} overflowX='hidden'>
     <Flex px={['24px', '0']} justify='flex-end'>
      <Button
       type='submit'
       variant='primary'
       isDisabled={!isDirty || !isValid}
       isLoading={isSubmitting}
       h='49px'
       width='200px'
       mt='30px'
      >
       {companyRegister ? 'Salvar' : 'Cadastrar'}
      </Button>
     </Flex>
    </Container>
   </Center>
  </form>
 );
}
